import React, { useState, useRef, useEffect, useCallback } from 'react';
import { X, CheckCircle } from 'lucide-react';
import { Product, Store } from '../services/orderLinkAPI';
import { getDirectImageUrl } from '../utils/imageUrl';
import ProductAttributeSelector, { ProductAttributeSelectorRef } from './ui/ProductAttributeSelector';

export interface PizzaPartData {
  product: Product;
  attributes: Record<string, any>;
  finalPrice: number;
}

interface PizzaPartSelectorModalProps {
  isOpen: boolean;
  selectedParts: PizzaPartData[];      // All parts already selected
  currentPartIndex: number;            // Which part we're selecting (0-based)
  totalParts: number;                  // Total parts needed (2, 3, or 4)
  availableProducts: Product[];
  store?: Store;
  onComplete: (part: PizzaPartData) => void;
  onCancel: () => void;
}

const PizzaPartSelectorModal: React.FC<PizzaPartSelectorModalProps> = ({
  isOpen,
  selectedParts,
  currentPartIndex,
  totalParts,
  availableProducts,
  store,
  onComplete,
  onCancel,
}) => {
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const attributeSelectorRef = useRef<ProductAttributeSelectorRef>(null);
  const [calculatedFinalPrice, setCalculatedFinalPrice] = useState<number>(0);

  const calculateFinalPrice = useCallback(() => {
    if (!selectedProduct || !attributeSelectorRef.current || selectedParts.length === 0) {
      setCalculatedFinalPrice(0);
      return;
    }

    const { finalPrice } = attributeSelectorRef.current.getSummary();

    if (store?.cobrar_preco_maior) {
      const allPrices = [...selectedParts.map(p => p.finalPrice), finalPrice];
      setCalculatedFinalPrice(Math.max(...allPrices));
    } else {
      setCalculatedFinalPrice(finalPrice);
    }
  }, [selectedProduct, selectedParts, store]);

  useEffect(() => {
    if (selectedProduct) {
      setTimeout(() => {
        calculateFinalPrice();
      }, 300);
    } else {
      setCalculatedFinalPrice(0);
    }
  }, [selectedProduct, calculateFinalPrice]);

  useEffect(() => {
    setSelectedProduct(null);
    setCalculatedFinalPrice(0);
  }, [currentPartIndex]);

  if (!isOpen) return null;

  const firstPart = selectedParts[0];
  const firstPartSize = firstPart.attributes.Tamanho;
  const firstPartCorte = firstPart.attributes.Corte;
  const firstPartBorda = firstPart.attributes.Borda;

  const allCutOptions = ['Inteira', 'Meia', 'Quarto', 'Terço'];
  const disabledCutOptions = allCutOptions.filter(cut => cut !== firstPartCorte);

  const selectedProductIds = selectedParts.map(part => part.product.id);

  const filteredProducts = availableProducts.filter(product => {
    if (product.product_type?.type !== 'Pizza') return false;

    const sizeOptions = product.attributes?.Tamanho;
    if (!sizeOptions || typeof sizeOptions !== 'object') return false;
    if (!Object.keys(sizeOptions).includes(firstPartSize)) return false;

    const corteOptions = product.attributes?.Corte;
    if (!corteOptions || typeof corteOptions !== 'object') return false;
    if (!Object.keys(corteOptions).includes(firstPartCorte)) return false;

    return !selectedProductIds.includes(product.id);
  });

  const handleProductSelect = (product: Product) => {
    setSelectedProduct(product);
  };

  const handleAddPart = () => {
    if (!selectedProduct || !attributeSelectorRef.current) return;

    const { attributes, finalPrice } = attributeSelectorRef.current.getSummary();
    const firstPart = selectedParts[0];

    const partAttributes = {
      ...attributes,
      Corte: firstPartCorte,
      Tamanho: firstPartSize,
      ...(firstPartBorda ? { Borda: firstPartBorda } : {}),
      _groupId: firstPart.attributes._groupId  // Preserve groupId from first part
    };

    onComplete({
      product: selectedProduct,
      attributes: partAttributes,
      finalPrice: finalPrice
    });
  };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 z-50 transition-opacity" />

      {/* Modal */}
      <div className="fixed inset-x-0 bottom-0 sm:inset-0 sm:flex sm:items-center sm:justify-center z-50">
        <div className="bg-white w-full sm:max-w-4xl sm:rounded-lg shadow-xl flex flex-col max-h-[90vh]">
          <div className="p-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  Escolha a parte {currentPartIndex + 1} de {totalParts}
                </h2>
                <p className="text-sm text-gray-500 mt-1">
                  Complete sua pizza escolhendo {totalParts === 2 ? 'outra metade' : totalParts === 3 ? 'outros terços' : 'outros quartos'}
                </p>
              </div>
              <button
                onClick={onCancel}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-600" />
              </button>
            </div>

            <div className="mt-4 space-y-2">
              {selectedParts.map((part, index) => (
                <div key={index} className="p-3 bg-primary-50 border border-primary-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-primary-600" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-primary-900">
                        Parte {index + 1} selecionada
                      </p>
                      <p className="text-sm text-primary-700">
                        {part.product.name} (Tamanho {part.attributes.Tamanho})
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Não há outras pizzas disponíveis neste tamanho
              </div>
            ) : !selectedProduct ? (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Selecione uma pizza do mesmo tamanho:
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {filteredProducts.map((product) => (
                    <button
                      key={product.id}
                      onClick={() => handleProductSelect(product)}
                      className="bg-white border-2 border-gray-200 hover:border-primary-500 rounded-lg p-4 text-left transition-all hover:shadow-md"
                    >
                      <div className="w-full h-24 bg-gray-50 rounded-md overflow-hidden mb-3">
                        <img
                          src={getDirectImageUrl(product.image_path)}
                          alt={product.name}
                          className="w-full h-full object-cover"
                          onError={(e: any) => { e.currentTarget.src = "/no_icon.png"; }}
                        />
                      </div>
                      <h3 className="font-semibold text-gray-900 text-sm mb-1">
                        {product.name}
                      </h3>
                      <p className="text-primary-600 font-bold text-sm">
                        R$ {product.price.toFixed(2).replace('.', ',')}
                      </p>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <div>
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {selectedProduct.name}
                  </h3>
                  <button
                    onClick={() => setSelectedProduct(null)}
                    className="text-sm text-primary-600 hover:text-primary-700"
                  >
                    Escolher outra pizza
                  </button>
                </div>

                <ProductAttributeSelector
                  key={selectedProduct.id}
                  ref={attributeSelectorRef}
                  product={selectedProduct}
                  mode="manual"
                  useRadioForCheckboxes={true}
                  useRadioForDropdowns={true}
                  initialValues={{
                    Corte: firstPartCorte,
                    Tamanho: firstPartSize,
                    ...(firstPartBorda ? { Borda: firstPartBorda } : {})
                  }}
                  hiddenAttributes={firstPartBorda ? ['Borda'] : []}
                  disabledAttributes={['Tamanho']}
                  disabledAttributeOptions={{
                    Corte: disabledCutOptions
                  }}
                  ignorePriceForAttributes={['Corte']}
                  hidePriceSummary={true}
                  onAttributesChange={() => {
                    calculateFinalPrice();
                  }}
                />

                {calculatedFinalPrice > 0 && (
                  <div className="mt-4 p-4 bg-gray-50 border border-gray-200 rounded-lg">
                    <div className="flex items-center justify-between">
                      <p className="text-sm font-medium text-gray-700">
                        Preço Final
                      </p>
                      <p className="text-xl font-bold text-primary-600">
                        R$ {calculatedFinalPrice.toFixed(2).replace('.', ',')}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {selectedProduct && (
            <div className="border-t border-gray-200 p-4 bg-gray-50">
              <button
                onClick={handleAddPart}
                className="w-full btn btn-primary text-lg py-3"
              >
                {currentPartIndex + 1 === totalParts ? 'Adicionar ao Carrinho' : 'Continuar'}
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default PizzaPartSelectorModal;
